import React, { useState } from 'react';
import { Box, Container, Typography, Grid, Button, Dialog, DialogTitle, DialogContent, Link } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Link as NavLink } from "react-scroll";

// Helper function to get AppBar height
const getAppBarHeight = () => 64;

export default function ResearchSection({ publications }) {
  const [showPublicationsDialog, setShowPublicationsDialog] = useState(false);
  
  if (!publications || publications.length === 0) return null;
  const appBarHeight = getAppBarHeight();

  // Split publications into featured (first 3) and all others
  const featuredPublications = publications.slice(0, 3);
  const allPublications = publications;

  return (
    <Box sx={{ 
      position: 'relative',
      paddingY: 10,
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      overflow: 'hidden'
    }}>
      {/* Subtle background pattern */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.03,
        backgroundImage: `radial-gradient(circle at 25px 25px, #2561b3 2px, transparent 0), radial-gradient(circle at 75px 75px, #2561b3 2px, transparent 0)`,
        backgroundSize: '100px 100px',
        backgroundPosition: '0 0, 50px 50px'
      }} />
      
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Main heading with sophisticated typography */}
        <Box sx={{ textAlign: 'center', marginBottom: 8 }}>
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              fontWeight: 800,
              background: 'linear-gradient(135deg, #2561b3 0%, #1e40af 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              marginBottom: 2,
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            Research Excellence
          </Typography>
          <Box sx={{ 
            width: 80, 
            height: 4, 
            background: 'linear-gradient(90deg, #2561b3, #60a5fa)',
            margin: '0 auto',
            borderRadius: 2
          }} />
        </Box>

        {/* Hero introduction section */}
        <Box sx={{ 
          maxWidth: '1000px', 
          marginX: 'auto', 
          marginBottom: 10,
          position: 'relative'
        }}>
          <Box sx={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: 4,
            padding: { xs: 4, md: 6 },
            border: '1px solid rgba(255,255,255,0.3)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Decorative corner elements */}
            <Box sx={{
              position: 'absolute',
              top: -20,
              right: -20,
              width: 100,
              height: 100,
              background: 'linear-gradient(135deg, #2561b3, #60a5fa)',
              borderRadius: '50%',
              opacity: 0.1
            }} />
            <Box sx={{
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 80,
              height: 80,
              background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
              borderRadius: '50%',
              opacity: 0.08
            }} />
            
            <Typography variant="h4" component="h3" sx={{ 
              textAlign: 'center',
              marginBottom: 4, 
              color: '#1e293b',
              fontWeight: 700,
              position: 'relative',
              zIndex: 2
            }}>
              Pioneering Research & Innovation
            </Typography>
            
            <Typography variant="h6" component="p" sx={{ 
              textAlign: 'center',
              marginBottom: 3, 
              color: '#475569',
              lineHeight: 1.7,
              fontWeight: 400,
              fontSize: '1.25rem'
            }}>
              At Datasky, we don't just follow trends—we create them. Our research-driven approach combines academic rigour with commercial insight to deliver breakthrough solutions.
            </Typography>
            
            <Typography variant="body1" component="p" sx={{ 
              textAlign: 'center',
              color: '#64748b',
              lineHeight: 1.8,
              fontSize: '1.1rem',
              maxWidth: '800px',
              marginX: 'auto'
            }}>
              From epidemiological breakthroughs to AI innovations, our multidisciplinary team transforms complex challenges into actionable intelligence that drives real-world impact.
            </Typography>
          </Box>
        </Box>

        {/* Publications showcase */}
        <Box sx={{ marginBottom: 12 }}>
          <Typography variant="h4" component="h3" sx={{ 
            textAlign: 'center',
            marginBottom: 6, 
            color: '#1e293b',
            fontWeight: 600,
            position: 'relative'
          }}>
            Featured Publications
            <Box sx={{
              position: 'absolute',
              bottom: -10,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 60,
              height: 3,
              backgroundColor: '#2561b3',
              borderRadius: 2
            }} />
          </Typography>
          
          <Grid container spacing={4}>
            {featuredPublications.map((pub, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{
                  height: '100%',
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                  borderRadius: 3,
                  padding: 4,
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(37, 97, 179, 0.15)',
                    '& .pub-number': {
                      background: 'linear-gradient(135deg, #2561b3, #60a5fa)',
                      color: 'white'
                    }
                  }
                }}>
                  {/* Publication number badge */}
                  <Box 
                    className="pub-number"
                    sx={{
                      position: 'absolute',
                      top: 20,
                      right: 20,
                      width: 35,
                      height: 35,
                      borderRadius: '50%',
                      background: '#e2e8f0',
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {index + 1}
                  </Box>
                  
                  <Typography variant="h6" component="h4" sx={{ 
                    marginBottom: 3,
                    marginTop: 1,
                    fontWeight: 600,
                    lineHeight: 1.4,
                    color: '#1e293b',
                    paddingRight: 5
                  }}>
                    <Link 
                      href={pub.Url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ 
                        color: 'inherit',
                        textDecoration: 'none',
                        '&:hover': { 
                          color: '#2561b3'
                        }
                      }}
                    >
                      {pub.Title}
                    </Link>
                  </Typography>
                  
                  <Typography variant="body2" sx={{ 
                    color: '#64748b',
                    fontStyle: 'italic',
                    fontSize: '0.95rem'
                  }}>
                    {pub.Authors}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ 
                    color: '#2561b3',
                    fontWeight: 600,
                    marginTop: 1
                  }}>
                    {pub.Year}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Button to open publications dialog */}
        <Box sx={{ textAlign: 'center', marginBottom: 8 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => setShowPublicationsDialog(true)}
            sx={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
              backdropFilter: 'blur(10px)',
              border: '2px solid #2561b3',
              color: '#2561b3',
              fontWeight: 700,
              paddingX: 4,
              paddingY: 1.5,
              fontSize: '1.1rem',
              borderRadius: 3,
              textTransform: 'none',
              boxShadow: '0 8px 25px rgba(37, 97, 179, 0.2)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #2561b3 0%, #1e40af 100%)',
                color: 'white',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 35px rgba(37, 97, 179, 0.4)'
              }
            }}
          >
            View Complete Publication List
          </Button>
        </Box>

        {/* Publications Dialog */}
        <Dialog 
          open={showPublicationsDialog} 
          onClose={() => setShowPublicationsDialog(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #2561b3 0%, #1e40af 100%)',
            color: 'white',
            textAlign: 'center',
            fontSize: '1.5rem',
            fontWeight: 700,
            position: 'relative',
            paddingY: 3
          }}>
            Complete Publication List
            <Button
              onClick={() => setShowPublicationsDialog(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'white',
                minWidth: 'auto',
                padding: 1
              }}
            >
              <CloseIcon />
            </Button>
          </DialogTitle>
          
          <DialogContent sx={{ paddingY: 3 }}>
            <Box>
              {allPublications.map((pub, index) => (
                <Box 
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 2,
                    marginBottom: 1,
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                    borderRadius: 2,
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 20px rgba(37, 97, 179, 0.1)',
                      background: 'linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)'
                    },
                    '&:last-child': {
                      marginBottom: 0
                    }
                  }}
                >
                  {/* Year badge */}
                  <Box sx={{
                    minWidth: 50,
                    height: 50,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #2561b3, #60a5fa)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '0.85rem',
                    marginRight: 2.5,
                    flexShrink: 0,
                    boxShadow: '0 3px 10px rgba(37, 97, 179, 0.3)'
                  }}>
                    {pub.Year}
                  </Box>
                  
                  {/* Publication details */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" component="h4" sx={{ 
                      marginBottom: 0.5,
                      fontWeight: 600,
                      lineHeight: 1.3,
                      color: '#1e293b',
                      fontSize: '1rem'
                    }}>
                      <Link 
                        href={pub.Url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        sx={{ 
                          color: 'inherit',
                          textDecoration: 'none',
                          '&:hover': { 
                            color: '#2561b3'
                          }
                        }}
                      >
                        {pub.Title}
                      </Link>
                    </Typography>
                    
                    <Typography variant="body2" sx={{ 
                      color: '#64748b',
                      fontStyle: 'italic',
                      fontSize: '0.9rem',
                      lineHeight: 1.4
                    }}>
                      {pub.Authors}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </DialogContent>
        </Dialog>

        {/* Premium CTA section */}
        <Box sx={{ 
          position: 'relative',
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          borderRadius: 4,
          padding: { xs: 6, md: 8 },
          textAlign: 'center',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          {/* Animated background elements */}
          <Box sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 200,
            height: 200,
            background: 'linear-gradient(135deg, rgba(37, 97, 179, 0.3), rgba(96, 165, 250, 0.2))',
            borderRadius: '50%',
            animation: 'float 6s ease-in-out infinite'
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -80,
            left: -80,
            width: 160,
            height: 160,
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 97, 179, 0.3))',
            borderRadius: '50%',
            animation: 'float 8s ease-in-out infinite reverse'
          }} />
          
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Typography variant="h3" component="h4" sx={{ 
              marginBottom: 2, 
              fontWeight: 700,
              color: 'white',
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}>
              Ready to Innovate Together?
            </Typography>
            
            <Typography variant="h6" component="p" sx={{ 
              marginBottom: 4,
              color: 'rgba(255, 255, 255, 0.8)',
              fontWeight: 400,
              maxWidth: '600px',
              marginX: 'auto'
            }}>
              Let's collaborate on groundbreaking research that shapes the future of your industry
            </Typography>
            
            <NavLink 
              to="contact-section" 
              spy
              smooth
              offset={-appBarHeight}
              duration={500}
              style={{ textDecoration: 'none' }}
            >
              <Button 
                variant="contained" 
                size="large" 
                sx={{
                  background: 'linear-gradient(135deg, #2561b3 0%, #3b82f6 100%)',
                  color: 'white',
                  fontWeight: 700,
                  paddingX: 6,
                  paddingY: 2.5,
                  fontSize: '1.1rem',
                  borderRadius: 3,
                  textTransform: 'none',
                  boxShadow: '0 8px 25px rgba(37, 97, 179, 0.4)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 35px rgba(37, 97, 179, 0.5)'
                  }
                }}
              >
                Start Your Project
              </Button>
            </NavLink>
          </Box>
        </Box>
      </Container>
      
      {/* Add keyframes for floating animation and fade-in */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes fadeIn {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Box>
  );
} 